import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { FileUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { DynamicHeightDirective } from '../../../shared/directives/dynamic-height.directive';
import { AmountFormatDirective } from '../../../shared/directives/amount-format.directive';
import { PhoneFormatDirective } from '../../../shared/directives/phone-format.directive';
import { ValidationService } from '../../../core/services/validation.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { CaseEnquiryService } from '../../../core/services/case-enquiry.service';
import { AttorneyService } from '../../attorney/services/attorney.service';
import { CaseService } from '../../../core/services/case.service';

@Component({
    selector: 'app-add-case-details',
    standalone: true,
    imports: [
    ReactiveFormsModule,
    RouterModule,
    DatePickerComponent,
    FileUploadComponent,
    DynamicHeightDirective,
    AmountFormatDirective,
    PhoneFormatDirective
],
    templateUrl: './add-case-details.component.html',
    styleUrl: './add-case-details.component.scss'
})
export class AddCaseDetailsComponent implements OnInit {
    detailsForm: FormGroup;
    enquiryId: string | number | null = null;
    today: Date = new Date();
    prefilledData: any = {
        attorney_name: '',
        firm_name: '',
        attorney_email: '',
        attorney_phone: '',
        client_name: '',
        basic_case_type: '',
        lien_details: ''
    };

    constructor(
        private fb: FormBuilder,
        private validationService: ValidationService,
        private router: Router,
        private route: ActivatedRoute,
        private snackbar: SnackbarService,
        private caseEnquiryService: CaseEnquiryService,
        private attorneyService: AttorneyService,
        private caseService: CaseService
    ) {
        this.detailsForm = this.fb.group({
            // Pre-filled (non-editable in UI, but kept in form state)
            attorney_name: [{ value: '', disabled: true }],
            firm_name: [{ value: '', disabled: true }],
            attorney_email: [{ value: '', disabled: true }],
            attorney_phone: [{ value: '', disabled: true }],
            client_name: [{ value: '', disabled: true }],
            basic_case_type: [{ value: '', disabled: true }],
            lien_details: [{ value: '', disabled: true }],

            // New Required Fields
            client_dob: [null, Validators.required],
            client_email: ['', [Validators.required, Validators.email]],
            client_contact: ['', Validators.required],
            patient_address: ['', Validators.required],
            same_address: [0],
            posting_address: [''],
            last_four_ssn: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
            hospital_name: ['', Validators.required],
            hospital_account_number: ['', Validators.required],
            current_lien_balance: [null, [Validators.required, Validators.min(0)]],
            date_of_service: [null, Validators.required],
            supporting_details: ['', Validators.required],
            reductions_requested: [null, Validators.required],
            reduction_amount: [null],

            // Document questions (Yes/No)
            has_partial_closing_statement: [null, Validators.required],
            has_itemized_bill: [null, Validators.required],
            has_hospital_claim_lien: [null, Validators.required],
            has_correspondence: [null, Validators.required],

            // Uploads (conditionally required when Yes)
            partial_closing_statement: [null],
            itemized_bill: [null],
            hospital_claim_lien: [null],
            correspondence: [null],

            // Explanations (conditionally required when No)
            no_partial_closing_reason: [''],
            no_itemized_bill_reason: [''],
            no_hospital_claim_reason: [''],
            no_correspondence_reason: ['']
        });
    }

    ngOnInit(): void {
        this.detailsForm.get('reductions_requested')?.valueChanges.subscribe(value => {
            const reductionAmountControl = this.detailsForm.get('reduction_amount');
            if (value === true) {
                reductionAmountControl?.setValidators([Validators.required, Validators.min(0)]);
            } else {
                reductionAmountControl?.clearValidators();
                reductionAmountControl?.setValue(null);
            }
            reductionAmountControl?.updateValueAndValidity();
        });

        // Document conditional validators
        this.setupDocumentToggle('has_partial_closing_statement', 'partial_closing_statement', 'no_partial_closing_reason');
        this.setupDocumentToggle('has_itemized_bill', 'itemized_bill', 'no_itemized_bill_reason');
        this.setupDocumentToggle('has_hospital_claim_lien', 'hospital_claim_lien', 'no_hospital_claim_reason');
        this.setupDocumentToggle('has_correspondence', 'correspondence', 'no_correspondence_reason');

        this.route.queryParams.subscribe(params => {
            const enquiryId = params['enquiryId'];
            this.enquiryId = enquiryId;
            if (enquiryId) {
                this.loadEnquiryDetails(enquiryId);
            } else {
                this.caseEnquiryService.getAcceptedEnquiries().subscribe({
                    next: (list) => {
                        const first = Array.isArray(list) && list.length ? list[0] : null;
                        if (!first) return;
                        const clientName = first.client_name || [first.client_first_name, first.client_last_name].filter(Boolean).join(' ');
                        const caseType = first.basic_injury_type || first.injury_type;
                        const att = (first as any).attorney || first;
                        const firmName = att.firm_name || att.attorney_firm || '';
                        const attName = att.attorney_name || [att.first_name, att.last_name].filter(Boolean).join(' ') || '';
                        const attEmail = att.attorney_email || '';
                        const attPhone = att.attorney_phone || '';
                        const prefilled = {
                            attorney_name: attName,
                            firm_name: firmName,
                            attorney_email: attEmail,
                            attorney_phone: attPhone,
                            client_name: clientName,
                            basic_case_type: caseType,
                            lien_details: first.lien_details || first.details || ''
                        };
                        this.prefilledData = prefilled;
                        this.detailsForm.patchValue(prefilled);
                    }
                });
            }
        });
    }

    loadEnquiryDetails(id: string | number) {
        this.caseEnquiryService.getEnquiryById(id).pipe(
            switchMap(data => of(data))
        ).subscribe({
            next: (data) => {
                const clientName = data.client_name || 
                                 [data.client_first_name, data.client_last_name].filter(Boolean).join(' ');
                
                const caseType = data.basic_injury_type || data.injury_type;

                // Handle potential nested attorney object or flat structure
                const att = data.attorney || data;
                const firmName = att.firm_name || att.attorney_firm || '';
                const attName = att.attorney_name || 
                              [att.first_name, att.last_name].filter(Boolean).join(' ') || '';
                const attEmail = att.attorney_email || '';
                const attPhone = att.attorney_phone || '';

                const prefilled = {
                    attorney_name: attName,
                    firm_name: firmName,
                    attorney_email: attEmail,
                    attorney_phone: attPhone,
                    client_name: clientName,
                    basic_case_type: caseType,
                    lien_details: data.lien_details || data.details || ''
                };

                const needsAttorneyFill = !attName && !firmName && !attEmail && !attPhone;
                if (needsAttorneyFill) {
                    this.caseEnquiryService.getAcceptedEnquiries().subscribe({
                        next: (list) => {
                            const match = (Array.isArray(list) ? list : []).find((item: any) => {
                                // Use loose equality to handle string/number mismatch
                                return item?.id == id || item?.case_id == id || item?.case_inquiry_id == id;
                            });
                            const source = match || null;
                            if (source) {
                                const sFirm = source.firm_name || source.attorney_firm || '';
                                const sName = source.attorney_name || [source.first_name, source.last_name].filter(Boolean).join(' ') || '';
                                const sEmail = source.attorney_email || '';
                                const sPhone = source.attorney_phone || '';
                                const merged = {
                                    ...prefilled,
                                    attorney_name: sName,
                                    firm_name: sFirm,
                                    attorney_email: sEmail,
                                    attorney_phone: sPhone
                                };
                                this.prefilledData = merged;
                                this.detailsForm.patchValue(merged);
                            } else {
                                this.prefilledData = prefilled;
                                this.detailsForm.patchValue(prefilled);
                            }
                        },
                        error: () => {
                            this.prefilledData = prefilled;
                            this.detailsForm.patchValue(prefilled);
                        }
                    });
                } else {
                    this.prefilledData = prefilled;
                    this.detailsForm.patchValue(prefilled);
                }
            },
            error: () => {
                this.snackbar.showError('Failed to load case inquiry details');
            }
        });
    }

    onSameAddressToggle(): void {
        const sameAddress = this.detailsForm.get('same_address')?.value;
        const postingControl = this.detailsForm.get('posting_address');
        if (sameAddress) {
            postingControl?.clearValidators();
            postingControl?.setValue('');
        } else {
            postingControl?.setValidators(Validators.required);
        }
        postingControl?.updateValueAndValidity();
    }

    getErrorMessage(controlName: string, label: string): string | null {
        const control = this.detailsForm.get(controlName);
        if (control && control.touched && control.errors) {
            if (control.errors['serverError']) {
                return control.errors['serverError'];
            }
            return this.validationService.getValidatorErrorMessage(control.errors, label);
        }
        return null;
    }

    handleCancel() {
        this.router.navigate(['/cases']);
    }

    handleSave() {
        if (this.detailsForm.valid) {
            if (!this.enquiryId) {
                this.snackbar.showError('Case Inquiry ID is missing');
                return;
            }

            const formValue = this.detailsForm.getRawValue();
            const formData = new FormData();

            // Append basic fields
            formData.append('case_inquiry_id', this.enquiryId.toString());
            
            // Format date if needed (assuming date picker returns Date object or ISO string)
            // If it returns a Date object, convert to YYYY-MM-DD
            let dob = formValue.client_dob;
            if (dob instanceof Date) {
                dob = dob.toISOString().split('T')[0];
            }
            formData.append('client_dob', dob);
            
            // Map other fields
            formData.append('client_email', formValue.client_email);
            formData.append('client_contact', formValue.client_contact);
            formData.append('client_phone', formValue.client_contact); // Keeping for backward compatibility if needed
            formData.append('patient_address', formValue.patient_address);
            formData.append('same_address', formValue.same_address ? '1' : '0');
            formData.append('posting_address', formValue.same_address ? formValue.patient_address : (formValue.posting_address || ''));
            formData.append('ssn_last4', formValue.last_four_ssn);
            formData.append('hospital_name', formValue.hospital_name);
            formData.append('hospital_account_number', formValue.hospital_account_number);
            let dateOfService = formValue.date_of_service;
            if (dateOfService instanceof Date) {
                dateOfService = dateOfService.toISOString().split('T')[0];
            }
            formData.append('date_of_admission', dateOfService);
            formData.append('current_lien_balance', formValue.current_lien_balance);
            formData.append('treatment_details', formValue.supporting_details);
            formData.append('reductions_requested', formValue.reductions_requested ? '1' : '0');
            formData.append('reduction_offered', formValue.reductions_requested ? 'true' : 'false');
            if (formValue.reductions_requested && formValue.reduction_amount) {
                formData.append('reduction_amount', formValue.reduction_amount);
            }
            formData.append('status', 'SUBMITTED');

            // Handle files
            const appendFiles = (key: string, controlName: string) => {
                const files = formValue[controlName];
                if (Array.isArray(files) && files.length > 0) {
                    files.forEach((file: File) => {
                        formData.append(key, file);
                    });
                }
            };

            // Document status flags (1 = yes/has file, 0 = no/has reason)
            formData.append('partial_closing_statement_status', formValue.has_partial_closing_statement ? '1' : '0');
            formData.append('bill_or_invoice_status', formValue.has_itemized_bill ? '1' : '0');
            formData.append('hosipital_claim_lien_status', formValue.has_hospital_claim_lien ? '1' : '0');
            formData.append('correspondence_lienholder_status', formValue.has_correspondence ? '1' : '0');

            if (formValue.has_partial_closing_statement) {
                appendFiles('partial_closing_statement[]', 'partial_closing_statement');
            } else {
                formData.append('partial_closing_statement_reason', formValue.no_partial_closing_reason || '');
            }

            if (formValue.has_itemized_bill) {
                appendFiles('bill_or_invoice[]', 'itemized_bill');
            } else {
                formData.append('bill_or_invoice_reason', formValue.no_itemized_bill_reason || '');
            }

            if (formValue.has_hospital_claim_lien) {
                appendFiles('hosipital_claim_lien[]', 'hospital_claim_lien');
            } else {
                formData.append('hosipital_claim_lien_reason', formValue.no_hospital_claim_reason || '');
            }

            if (formValue.has_correspondence) {
                appendFiles('correspondence_lienholder[]', 'correspondence');
            } else {
                formData.append('correspondence_lienholder_reason', formValue.no_correspondence_reason || '');
            }

            this.caseService.submitCase(formData).subscribe({
                next: (res) => {
                    this.snackbar.show('Case details submitted successfully', 'success');
                    this.router.navigate(['/cases']);
                },
                error: (err) => {
                    if (err.status === 422 && err.error && err.error.errors) {
                        const serverErrors = err.error.errors;
                        Object.keys(serverErrors).forEach(key => {
                            const control = this.detailsForm.get(key);
                            if (control) {
                                control.setErrors({ serverError: serverErrors[key][0] });
                                control.markAsTouched();
                            }
                        });
                        this.snackbar.show('Please fix the validation errors', 'error');
                    } else {
                        this.snackbar.show('Failed to submit case details', 'error');
                    }
                }
            });
        } else {
            this.markFormGroupTouched(this.detailsForm);
            this.snackbar.show('Please complete all required fields and uploads', 'error');
        }
    }

    private setupDocumentToggle(radioControl: string, uploadControl: string, reasonControl: string) {
        this.detailsForm.get(radioControl)?.valueChanges.subscribe(value => {
            const upload = this.detailsForm.get(uploadControl);
            const reason = this.detailsForm.get(reasonControl);
            if (value === true) {
                upload?.setValidators(Validators.required);
                reason?.clearValidators();
                reason?.setValue('');
            } else if (value === false) {
                upload?.clearValidators();
                upload?.setValue(null);
                reason?.setValidators(Validators.required);
            }
            upload?.updateValueAndValidity();
            reason?.updateValueAndValidity();
        });
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
            if ((control as any).controls) {
                this.markFormGroupTouched(control as FormGroup);
            }
        });
    }
}
